import { useEditorElementFocus, useTabStrip, usePanelPlacement, usePanelKeyboardFocus } from "./editor/react.tsx";
import { SplitPane } from "./SplitPane";
import {
  CaretLeftIcon,
  CaretRightIcon,
  ColumnsPlusRightIcon,
  RowsPlusBottomIcon
} from "@phosphor-icons/react";
import { type ComponentPropsWithoutRef, type MouseEvent as ReactMouseEvent } from "react";
import { createPortal } from "react-dom";
import {
  EDITOR_TAB_DRAG_MIME,
  EDITOR_TAB_DRAG_VERSION,
  findAdjacentGroup,
  readEditorTabDragPayload,
  writeEditorTabDragPayload,
  type EditorAdjacentDirection,
  type EditorGroupNode,
  type EditorLayoutNode,
  type EditorSplitDirection,
  type EditorTabDragPayload
} from "./editor-layout-model";
import {
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type DragEvent,
  type KeyboardEvent,
  type ReactNode
} from "react";

export const EDITOR_LAYOUT_DRAG_MIME = EDITOR_TAB_DRAG_MIME;
export const EDITOR_LAYOUT_DRAG_VERSION = EDITOR_TAB_DRAG_VERSION;
export type EditorLayoutSplitDirection = EditorSplitDirection;
export type EditorLayoutGroupNode = EditorGroupNode;
export type EditorLayoutDragPayload = EditorTabDragPayload;
export type { EditorLayoutNode } from "./editor-layout-model";

export type EditorLayoutDropPosition = "before" | "after" | "append";

/** Where a dragged tab would be inserted. */
export interface EditorLayoutDropTarget {
  readonly groupId: string;
  /** Omitted for an empty group or an append drop. */
  readonly tabId?: string;
  readonly position: EditorLayoutDropPosition;
}

export interface EditorLayoutTabRenderContext<TTab> {
  readonly tab: TTab;
  readonly tabId: string;
  readonly groupId: string;
  readonly panelId: string;
  readonly active: boolean;
  readonly focused: boolean;
}

export interface EditorLayoutGroupRenderContext<TTab> {
  readonly groupId: string;
  readonly tabs: readonly TTab[];
  readonly activeTabId: string | null;
  readonly focused: boolean;
  readonly isRoot: boolean;
}

export interface EditorLayoutTabReorderDetails {
  readonly tabId: string;
  readonly groupId: string;
  readonly fromIndex: number;
  /** Index after the move is applied. */
  readonly toIndex: number;
}

export interface EditorLayoutAdjacentMoveDetails {
  readonly tabId: string;
  readonly sourceGroupId: string;
  readonly targetGroupId: string;
  readonly direction: EditorAdjacentDirection;
}

export interface EditorLayoutProps<TTab> {
  /** Controlled recursive layout tree. */
  readonly node: EditorLayoutNode;
  /** Canonical tab descriptors. Group nodes contain IDs into this collection. */
  readonly tabs: readonly TTab[];
  readonly getTabId: (tab: TTab) => string;
  readonly getTabLabel: (tab: TTab) => ReactNode;
  readonly getTabAriaLabel?: (tab: TTab) => string;
  readonly getTabIcon?: (tab: TTab) => ReactNode;
  /** Render every tab panel; inactive panels remain mounted and are hidden. */
  readonly renderTab: (
    tab: TTab,
    context: EditorLayoutTabRenderContext<TTab>
  ) => ReactNode;

  /** A controlled focused group. Omit to use the first group plus local focus. */
  readonly focusedGroupId?: string | null;
  readonly defaultFocusedGroupId?: string;
  readonly onGroupFocus?: (groupId: string) => void;
  readonly getGroupLabel?: (groupId: string, node: EditorLayoutGroupNode) => string;

  readonly onSelectTab?: (tabId: string, groupId: string) => void;
  readonly onCloseTab?: (tabId: string, groupId: string) => void;
  readonly isTabCloseable?: (tab: TTab, groupId: string) => boolean;

  /** Called when a group chrome split command is invoked. */
  readonly onSplit?: (
    groupId: string,
    direction: EditorLayoutSplitDirection
  ) => void;
  readonly onSplitRatioChange?: (splitId: string, ratio: number) => void;
  /** Called only for empty, non-root groups. */
  readonly onCloseGroup?: (groupId: string) => void;
  /**
   * Hide a tab from the strip while keeping its surface and layout state.
   * Receives the group's active tab so visibility can depend on which surface
   * the group is currently showing.
   */
  readonly hideTabFromStrip?: (
    tabId: string,
    activeTabId: string | null
  ) => boolean;
  /** Extra entries appended to the group context menu, below a divider. */
  readonly renderMenuItems?: (context: {
    groupId: string;
    close: () => void;
  }) => ReactNode;
  readonly canCloseGroup?: (groupId: string) => boolean;
  readonly renderGroupActions?: (
    context: EditorLayoutGroupRenderContext<TTab>
  ) => ReactNode;

  /** Keyboard equivalent for moving a tab within one group. */
  readonly onTabReorder?: (details: EditorLayoutTabReorderDetails) => void;
  /** Keyboard equivalent for moving the active tab to a neighboring group. */
  readonly onMoveTabToAdjacentGroup?: (
    details: EditorLayoutAdjacentMoveDetails
  ) => void;

  readonly onTabDragStart?: (
    payload: EditorLayoutDragPayload,
    event: DragEvent<HTMLElement>
  ) => void;
  readonly onTabDragOver?: (
    payload: EditorLayoutDragPayload,
    target: EditorLayoutDropTarget,
    event: DragEvent<HTMLElement>
  ) => void;
  /** The consumer owns the atomic remove/insert/activate operation. */
  readonly onTabDrop?: (
    payload: EditorLayoutDragPayload,
    target: EditorLayoutDropTarget,
    event: DragEvent<HTMLElement>
  ) => void;
  readonly onTabDragEnd?: (
    payload: EditorLayoutDragPayload,
    event: DragEvent<HTMLElement>
  ) => void;

  readonly splitMin?: number;
  readonly splitMax?: number;
  readonly defaultSplitRatio?: number;
  readonly ariaLabel?: string;
  readonly className?: string;
}

interface ActiveDrop {
  readonly payload: EditorLayoutDragPayload;
  readonly target: EditorLayoutDropTarget;
}

function joinClassNames(...classNames: Array<string | undefined>): string {
  return classNames.filter(Boolean).join(" ");
}

function domIdPart(value: string): string {
  // encodeURIComponent is injective for strings and keeps IDs safe to use in
  // aria-controls/aria-labelledby without coupling the host to an ID format.
  return encodeURIComponent(value);
}

function groupIdsInDocumentOrder(node: EditorLayoutNode): string[] {
  if (node.kind === "group") return [node.id];
  return [
    ...groupIdsInDocumentOrder(node.children[0]),
    ...groupIdsInDocumentOrder(node.children[1])
  ];
}

function findGroup(
  node: EditorLayoutNode,
  groupId: string
): EditorLayoutGroupNode | undefined {
  if (node.kind === "group") return node.id === groupId ? node : undefined;
  return (
    findGroup(node.children[0], groupId) ?? findGroup(node.children[1], groupId)
  );
}

function firstGroupId(node: EditorLayoutNode): string | null {
  return node.kind === "group" ? node.id : firstGroupId(node.children[0]);
}

/**
 * Read and validate an editor-layout drag payload. External text/plain drops
 * intentionally return null; callers should not mutate layout for them.
 */
export function readEditorLayoutDragPayload(
  dataTransfer: DataTransfer | null | undefined
): EditorLayoutDragPayload | null {
  return dataTransfer ? readEditorTabDragPayload(dataTransfer) : null;
}

function writeDragPayload(
  dataTransfer: DataTransfer,
  payload: EditorLayoutDragPayload
): void {
  writeEditorTabDragPayload(dataTransfer, payload);
  // Keep a readable fallback for browser tooling while validating only the
  // application-specific MIME type on every drop.
  dataTransfer.setData("text/plain", payload.tabId);
}

function isInside(event: DragEvent<HTMLElement>): boolean {
  const relatedTarget = event.relatedTarget;
  return (
    typeof Node !== "undefined" &&
    relatedTarget instanceof Node &&
    event.currentTarget.contains(relatedTarget)
  );
}

/**
 * Horizontally-scrollable tab strip. When the tabs overflow, edge arrow buttons
 * appear to page through them; a vertical mouse wheel over the strip scrolls it
 * horizontally so all tabs are reachable without a visible scrollbar.
 */
function EditorTabStrip({
  tablistProps,
  onContextMenu,
  children
}: {
  tablistProps: ComponentPropsWithoutRef<"div"> & {
    [key: `data-${string}`]: string;
  };
  onContextMenu?: (event: ReactMouseEvent<HTMLDivElement>) => void;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [edges, setEdges] = useState({ left: false, right: false });

  const { measure, page, wheel } = useTabStrip(ref, value => setEdges(previous =>
    previous.left === value.left && previous.right === value.right ? previous : value));

  return (
    <div className="relative flex min-w-0 flex-1 items-stretch">
      {edges.left ? (
        <button
          type="button"
          aria-label="Scroll tabs left"
          data-editor-layout-tab-scroll="left"
          onClick={() => page(-1)}
          className="flex w-5 shrink-0 cursor-pointer items-center justify-center border-r border-kumo-line/60 bg-kumo-base text-kumo-subtle hover:text-kumo-default"
        >
          <CaretLeftIcon size={12} weight="bold" />
        </button>
      ) : null}
      <div
        {...tablistProps}
        ref={ref}
        onContextMenu={onContextMenu}
        onScroll={measure}
        onWheel={event => wheel(event.deltaX, event.deltaY)}
      >
        {children}
      </div>
      {edges.right ? (
        <button
          type="button"
          aria-label="Scroll tabs right"
          data-editor-layout-tab-scroll="right"
          onClick={() => page(1)}
          className="flex w-5 shrink-0 cursor-pointer items-center justify-center border-l border-kumo-line/60 bg-kumo-base text-kumo-subtle hover:text-kumo-default"
        >
          <CaretRightIcon size={12} weight="bold" />
        </button>
      ) : null}
    </div>
  );
}

function StableTabPanel({
  host,
  container,
  destinationId,
  panelId,
  tabDomId,
  active,
  onFocus,
  children
}: {
  host: HTMLElement;
  container: HTMLElement | null;
  destinationId: string;
  panelId: string;
  tabDomId: string;
  active: boolean;
  onFocus: () => void;
  children: ReactNode;
}) {
  const focusTab = usePanelKeyboardFocus(container, host);
  const [mount] = useState<HTMLElement | null>(() =>
    typeof document === "undefined" ? null : document.createElement("div")
  );

  useLayoutEffect(() => {
    if (!mount || mount.parentElement === host) return;
    mount.className = "min-h-0 overflow-hidden";
    mount.tabIndex = -1;
    if (mount.isConnected) {
      host.moveBefore(mount, null);
    } else {
      host.appendChild(mount);
    }
  });

  usePanelPlacement(mount, container, destinationId);

  useLayoutEffect(
    () => () => {
      mount?.remove();
    },
    [mount]
  );

  useLayoutEffect(() => {
    if (mount) mount.hidden = !active;
  }, [active, mount]);

  if (!mount) return null;
  return createPortal(
    <div
      id={panelId}
      role="tabpanel"
      aria-labelledby={tabDomId}
      hidden={!active}
      tabIndex={active ? 0 : -1}
      data-editor-layout-panel=""
      data-editor-layout-destination={destinationId}
      onFocusCapture={onFocus}
      onPointerDown={onFocus}
      onKeyDown={(event) => {
        if (
          event.target === event.currentTarget &&
          event.key === "Tab" &&
          event.shiftKey
        ) {
          event.preventDefault();
          focusTab({ _tag: "Tab", id: tabDomId });
        }
      }}
      className="h-full min-h-0 overflow-hidden"
    >
      {children}
    </div>,
    mount
  );
}

export function EditorLayout<TTab>({
  node,
  tabs,
  getTabId,
  getTabLabel,
  getTabAriaLabel,
  getTabIcon,
  renderTab,
  focusedGroupId,
  defaultFocusedGroupId,
  onGroupFocus,
  getGroupLabel,
  onSelectTab,
  onCloseTab,
  isTabCloseable,
  onSplit,
  onSplitRatioChange,
  onCloseGroup,
  hideTabFromStrip,
  renderMenuItems,
  canCloseGroup,
  renderGroupActions,
  onTabReorder,
  onMoveTabToAdjacentGroup,
  onTabDragStart,
  onTabDragOver,
  onTabDrop,
  onTabDragEnd,
  splitMin = 15,
  splitMax = 85,
  defaultSplitRatio = 50,
  ariaLabel = "Editor layout",
  className
}: EditorLayoutProps<TTab>) {
  const instanceId = useId();
  const layoutRef = useRef<HTMLDivElement | null>(null), scheduleFocus = useEditorElementFocus(layoutRef);
  const [layoutRoot, setLayoutRoot] = useState<HTMLDivElement | null>(null);
  const bindLayout = useMemo(() => (element: HTMLDivElement | null) => { layoutRef.current = element; setLayoutRoot(element); }, []);
  const tabsById = useMemo(() => {
    const result = new Map<string, TTab>();
    for (const tab of tabs) {
      const tabId = getTabId(tab);
      if (!result.has(tabId)) result.set(tabId, tab);
    }
    return result;
  }, [getTabId, tabs]);
  const groupIds = useMemo(() => groupIdsInDocumentOrder(node), [node]);
  // Right-click menu for group actions; replaces the split buttons.
  const [groupMenu, setGroupMenu] = useState<{
    groupId: string;
    x: number;
    y: number;
  } | null>(null);
  const [uncontrolledFocusedGroupId, setUncontrolledFocusedGroupId] = useState<
    string | null
  >(
    () =>
      defaultFocusedGroupId ??
      (groupIds.includes(firstGroupId(node) ?? "")
        ? firstGroupId(node)
        : (groupIds[0] ?? null))
  );
  const activeFocusedGroupId =
    focusedGroupId !== undefined
      ? focusedGroupId
      : uncontrolledFocusedGroupId;
  const [stableSurfaceHost] = useState<HTMLDivElement | null>(() => {
    if (typeof document === "undefined") return null;
    const host = document.createElement("div");
    host.dataset.editorLayoutStableSurfaces = "";
    host.className = "contents";
    return host;
  });
  const focusPanel = usePanelKeyboardFocus(layoutRoot, stableSurfaceHost);
  const dragPayloadRef = useRef<EditorLayoutDragPayload | null>(null);
  const [activeDrop, setActiveDrop] = useState<ActiveDrop | null>(null);

  useLayoutEffect(() => {
    if (!stableSurfaceHost) return;
    const parent = document.body;
    if (stableSurfaceHost.parentElement === parent) return;
    if (stableSurfaceHost.isConnected) {
      parent.moveBefore(stableSurfaceHost, null);
    } else {
      parent.appendChild(stableSurfaceHost);
    }
  }, [stableSurfaceHost]);

  useLayoutEffect(
    () => () => {
      stableSurfaceHost?.remove();
    },
    [stableSurfaceHost]
  );

  function focusGroup(groupId: string): void {
    setUncontrolledFocusedGroupId(groupId);
    onGroupFocus?.(groupId);
  }

  function isKnownPayload(payload: EditorLayoutDragPayload): boolean {
    const sourceGroup = findGroup(node, payload.sourceGroupId);
    return Boolean(
      sourceGroup &&
        sourceGroup.tabs.includes(payload.tabId) &&
        tabsById.has(payload.tabId)
    );
  }

  function getDropPayload(
    event: DragEvent<HTMLElement>,
    requireTransferPayload = false
  ): EditorLayoutDragPayload | null {
    const activePayload = dragPayloadRef.current;
    if (!activePayload) return null;
    const payload = readEditorLayoutDragPayload(event.dataTransfer);
    if (!payload) {
      return requireTransferPayload || !isKnownPayload(activePayload)
        ? null
        : activePayload;
    }
    if (
      payload.version !== activePayload.version ||
      payload.tabId !== activePayload.tabId ||
      payload.sourceGroupId !== activePayload.sourceGroupId
    ) {
      return null;
    }
    return isKnownPayload(payload) ? payload : null;
  }

  function setDragTarget(
    event: DragEvent<HTMLElement>,
    payload: EditorLayoutDragPayload,
    target: EditorLayoutDropTarget
  ): void {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
    setActiveDrop({ payload, target });
    onTabDragOver?.(payload, target, event);
  }

  function onTabDragStartInternal(
    groupId: string,
    tabId: string,
    event: DragEvent<HTMLElement>
  ): void {
    const payload: EditorLayoutDragPayload = {
      version: EDITOR_LAYOUT_DRAG_VERSION,
      tabId,
      sourceGroupId: groupId
    };
    dragPayloadRef.current = payload;
    focusGroup(groupId);
    event.dataTransfer.effectAllowed = "move";
    try {
      writeDragPayload(event.dataTransfer, payload);
    } catch {
      dragPayloadRef.current = null;
      return;
    }
    onTabDragStart?.(payload, event);
  }

  function onTabDragEndInternal(
    event: DragEvent<HTMLElement>
  ): void {
    const payload = dragPayloadRef.current;
    dragPayloadRef.current = null;
    setActiveDrop(null);
    if (payload) onTabDragEnd?.(payload, event);
  }

  function onTabDragLeaveInternal(event: DragEvent<HTMLElement>): void {
    if (!isInside(event)) setActiveDrop(null);
  }

  function onDropInternal(
    target: EditorLayoutDropTarget,
    event: DragEvent<HTMLElement>
  ): void {
    const payload = getDropPayload(event, true);
    if (!payload) return;
    event.preventDefault();
    event.stopPropagation();
    setActiveDrop(null);
    if (payload.tabId === target.tabId) return;
    onTabDrop?.(payload, target, event);
    focusGroup(target.groupId);
    focusTab(target.groupId, payload.tabId);
  }

  function selectTab(tabId: string, groupId: string): void {
    focusGroup(groupId);
    onSelectTab?.(tabId, groupId);
  }

  function focusElement(id: string): void { scheduleFocus(id); }

  function tabDomId(groupId: string, tabId: string): string {
    return `${instanceId}-tab-${domIdPart(groupId)}-${domIdPart(tabId)}`;
  }

  function groupActionDomId(groupId: string): string {
    return `${instanceId}-group-actions-${domIdPart(groupId)}`;
  }

  function focusTab(groupId: string, tabId: string): void {
    focusElement(tabDomId(groupId, tabId));
  }

  function focusGroupAction(groupId: string): void {
    focusElement(groupActionDomId(groupId));
  }

  function onTabKeyDown(
    event: KeyboardEvent<HTMLDivElement>,
    group: EditorLayoutGroupNode,
    tabId: string,
    tabIndex: number,
    visibleTabIds: readonly string[],
    allTabIds: readonly string[]
  ): void {
    const key = event.key;
    const isModifierReorder = event.altKey && event.shiftKey;
    if (
      isModifierReorder &&
      onMoveTabToAdjacentGroup &&
      (key === "ArrowLeft" ||
        key === "ArrowRight" ||
        key === "ArrowUp" ||
        key === "ArrowDown")
    ) {
      const reorderDelta = key === "ArrowLeft" ? -1 : key === "ArrowRight" ? 1 : 0;
      if (reorderDelta !== 0) {
        const targetTabId = visibleTabIds[tabIndex + reorderDelta];
        if (targetTabId) {
          event.preventDefault();
          focusGroup(group.id);
          onTabReorder?.({
            tabId,
            groupId: group.id,
            fromIndex: allTabIds.indexOf(tabId),
            toIndex: allTabIds.indexOf(targetTabId)
          });
          focusTab(group.id, tabId);
          return;
        }
      }

      const direction: EditorAdjacentDirection =
        key === "ArrowLeft"
          ? "left"
          : key === "ArrowRight"
            ? "right"
            : key === "ArrowUp"
              ? "up"
              : "down";
      const targetGroupId = findAdjacentGroup(
        { root: node, focusedGroupId: group.id },
        group.id,
        direction
      );
      if (!targetGroupId) return;
      event.preventDefault();
      onMoveTabToAdjacentGroup({
        tabId,
        sourceGroupId: group.id,
        targetGroupId,
        direction
      });
      focusGroup(targetGroupId);
      focusTab(targetGroupId, tabId);
      return;
    }

    if (
      isModifierReorder &&
      (key === "ArrowLeft" || key === "ArrowRight")
    ) {
      const delta = key === "ArrowLeft" ? -1 : 1;
      const targetTabId = visibleTabIds[tabIndex + delta];
      if (!targetTabId) return;
      event.preventDefault();
      focusGroup(group.id);
      onTabReorder?.({
        tabId,
        groupId: group.id,
        fromIndex: allTabIds.indexOf(tabId),
        toIndex: allTabIds.indexOf(targetTabId)
      });
      focusTab(group.id, tabId);
      return;
    }

    let nextIndex: number | undefined;
    if (key === "ArrowLeft")
      nextIndex =
        (tabIndex - 1 + visibleTabIds.length) % visibleTabIds.length;
    if (key === "ArrowRight")
      nextIndex = (tabIndex + 1) % visibleTabIds.length;
    if (key === "Home") nextIndex = 0;
    if (key === "End") nextIndex = visibleTabIds.length - 1;
    if (nextIndex !== undefined && visibleTabIds.length > 0) {
      event.preventDefault();
      const nextTabId = visibleTabIds[nextIndex];
      if (nextTabId) {
        selectTab(nextTabId, group.id);
        focusTab(group.id, nextTabId);
      }
      return;
    }

    if (key === "Enter" || key === " ") {
      event.preventDefault();
      selectTab(tabId, group.id);
    }
  }

  function renderGroup(
    group: EditorLayoutGroupNode,
    isRoot: boolean
  ): ReactNode {
    const tabIds = group.tabs.filter((tabId) => tabsById.has(tabId));
    const stripTabIds = hideTabFromStrip
      ? tabIds.filter(
          (tabId) => !hideTabFromStrip(tabId, group.activeTabId)
        )
      : tabIds;
    const groupTabs = tabIds
      .map((tabId) => tabsById.get(tabId))
      .filter((tab): tab is TTab => tab !== undefined);
    const activeTabId =
      group.activeTabId && tabIds.includes(group.activeTabId)
        ? group.activeTabId
        : (tabIds[0] ?? null);
    const focused = activeFocusedGroupId === group.id;
    const groupLabel =
      getGroupLabel?.(group.id, group) ?? `Editor group ${group.id}`;
    const groupContext: EditorLayoutGroupRenderContext<TTab> = {
      groupId: group.id,
      tabs: groupTabs,
      activeTabId,
      focused,
      isRoot
    };
    // The primary (first in document order) group carries the action toolbar;
    // secondary panels show only their tab strip. Emptied secondary panels are
    // auto-collapsed by the host, so no per-group close control is needed.
    const isPrimary = group.id === groupIds[0];
    const tablistDropActive =
      activeDrop?.target.groupId === group.id &&
      activeDrop.target.position === "append";

    return (
      <section
        key={group.id}
        aria-label={groupLabel}
        data-editor-layout-group=""
        data-group-id={group.id}
        data-focused={focused ? "true" : "false"}
        onFocusCapture={() => focusGroup(group.id)}
        onPointerDown={() => focusGroup(group.id)}
        className={joinClassNames(
          "flex h-full min-h-0 min-w-0 flex-col overflow-hidden border border-transparent",
          focused ? "border-kumo-brand/30" : "",
          className
        )}
      >
        <div className="flex h-8 shrink-0 min-w-0 items-stretch border-b border-kumo-line">
        <EditorTabStrip
          onContextMenu={(event) => {
            // Native menus stay available inside editable surfaces; the strip
            // itself owns layout actions.
            event.preventDefault();
            focusGroup(group.id);
            setGroupMenu({ groupId: group.id, x: event.clientX, y: event.clientY });
          }}
          tablistProps={{
            role: "tablist",
            "aria-label": `${groupLabel} tabs`,
            "aria-orientation": "horizontal",
            "data-editor-layout-tablist": "",
            "data-group-id": group.id,
            "data-drop-active": tablistDropActive ? "true" : "false",
            onDragOver: (event) => {
              // Tab-level handlers own before/after insertion. The strip itself
              // is the append target for its remaining empty space.
              if (event.target !== event.currentTarget) return;
              const payload = getDropPayload(event);
              if (!payload) return;
              setDragTarget(event, payload, {
                groupId: group.id,
                position: "append"
              });
            },
            onDragLeave: onTabDragLeaveInternal,
            onDrop: (event) =>
              onDropInternal(
                { groupId: group.id, position: "append" },
                event
              ),
            style: tablistDropActive
              ? { boxShadow: "inset -2px 0 var(--color-kumo-brand)" }
              : undefined,
            className:
              "flex min-w-0 flex-1 items-stretch gap-0.5 overflow-x-auto overflow-y-hidden px-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          }}
        >
          {stripTabIds.map((tabId, index) => {
            const tab = tabsById.get(tabId);
            if (!tab) return null;
            const panelId = `${instanceId}-panel-${domIdPart(tabId)}`;
            const currentTabDomId = tabDomId(group.id, tabId);
            const active = activeTabId === tabId;
            const tabLabel = getTabLabel(tab);
            const tabAriaLabel = getTabAriaLabel?.(tab) ?? tabId;
            const drop =
              activeDrop?.target.groupId === group.id &&
              activeDrop.target.tabId === tabId
                ? activeDrop.target.position
                : undefined;
            const closeable = Boolean(
              onCloseTab && (isTabCloseable?.(tab, group.id) ?? true)
            );

            return (
              <div
                key={tabId}
                data-editor-layout-tab-container=""
                data-tab-id={tabId}
                style={
                  drop === "before"
                    ? { boxShadow: "inset 2px 0 var(--color-kumo-brand)" }
                    : drop === "after"
                      ? { boxShadow: "inset -2px 0 var(--color-kumo-brand)" }
                      : undefined
                }
                className={joinClassNames(
                  "group/tab my-[3px] flex shrink-0 items-stretch rounded-md border",
                  active
                    ? "border-kumo-line bg-kumo-base text-kumo-default shadow-sm"
                    : "border-transparent text-kumo-subtle hover:bg-kumo-fill/70 hover:text-kumo-default"
                )}
              >
                <div
                  id={currentTabDomId}
                  role="tab"
                  aria-selected={active}
                  aria-controls={panelId}
                  aria-label={tabAriaLabel}
                  aria-posinset={index + 1}
                  aria-setsize={stripTabIds.length}
                  aria-keyshortcuts="Alt+Shift+ArrowLeft Alt+Shift+ArrowRight Alt+Shift+ArrowUp Alt+Shift+ArrowDown"
                  tabIndex={active ? 0 : -1}
                  draggable
                  data-editor-layout-tab=""
                  onClick={() => selectTab(tabId, group.id)}
                  onKeyDown={(event) =>
                    onTabKeyDown(
                      event,
                      group,
                      tabId,
                      index,
                      stripTabIds,
                      tabIds
                    )
                  }
                  onDragStart={(event) =>
                    onTabDragStartInternal(group.id, tabId, event)
                  }
                  onDragOver={(event) => {
                    event.stopPropagation();
                    const payload = getDropPayload(event);
                    if (!payload) return;
                    const bounds = event.currentTarget.getBoundingClientRect();
                    const position =
                      event.clientX < bounds.left + bounds.width / 2
                        ? "before"
                        : "after";
                    setDragTarget(event, payload, {
                      groupId: group.id,
                      tabId,
                      position
                    });
                  }}
                  onDragLeave={onTabDragLeaveInternal}
                  onDrop={(event) => {
                    event.stopPropagation();
                    const dropPosition =
                      activeDrop?.target.groupId === group.id &&
                      activeDrop.target.tabId === tabId
                        ? activeDrop.target.position
                        : "before";
                    onDropInternal(
                      { groupId: group.id, tabId, position: dropPosition },
                      event
                    );
                  }}
                  onDragEnd={onTabDragEndInternal}
                  title={`${tabAriaLabel} — drag to reorder or move`}
                  className="flex min-w-0 cursor-grab items-center gap-1.5 py-1 pl-3 pr-1 text-xs active:cursor-grabbing"
                >
                  {getTabIcon?.(tab)}
                  <span className="truncate">{tabLabel}</span>
                </div>
                {closeable ? (
                  <button
                    id={`${currentTabDomId}-close`}
                    type="button"
                    draggable={false}
                    aria-label={`Close ${tabAriaLabel}`}
                    title={`Close ${tabAriaLabel}`}
                    onKeyDown={(event) => {
                      if (
                        event.key === "Tab" &&
                        !event.shiftKey &&
                        active
                      ) {
                        focusPanel({ _tag: "Panel", id: panelId }, () => event.preventDefault());
                      }
                    }}
                    onClick={(event) => {
                      event.stopPropagation();
                      const currentIndex = tabIds.indexOf(tabId);
                      const nextTabId =
                        tabIds[currentIndex + 1] ?? tabIds[currentIndex - 1];
                      focusGroup(group.id);
                      onCloseTab?.(tabId, group.id);
                      if (nextTabId) focusTab(group.id, nextTabId);
                      else focusGroupAction(group.id);
                    }}
                    className={joinClassNames(
                      "mx-1 flex h-4 w-4 shrink-0 cursor-pointer items-center justify-center self-center rounded-full text-[13px] leading-none hover:bg-kumo-elevated hover:!opacity-100 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-kumo-brand",
                      active
                        ? "opacity-60"
                        : "opacity-0 group-hover/tab:opacity-60"
                    )}
                  >
                    ×
                  </button>
                ) : null}
              </div>
            );
          })}
        </EditorTabStrip>
        {isPrimary && renderGroupActions ? (
          <div className="flex shrink-0 items-center gap-0.5 border-l border-kumo-line px-1">
            {renderGroupActions(groupContext)}
          </div>
        ) : null}
        </div>

        {tabIds.length === 0 ? (
          <div
            aria-label="Empty editor group; drop a tab here"
            data-editor-layout-empty-drop-target=""
            data-drop-active={
              activeDrop?.target.groupId === group.id ? "true" : "false"
            }
            onDragOver={(event) => {
              const payload = getDropPayload(event);
              if (!payload) return;
              setDragTarget(event, payload, {
                groupId: group.id,
                position: "append"
              });
            }}
            onDragLeave={onTabDragLeaveInternal}
            onDrop={(event) =>
              onDropInternal(
                { groupId: group.id, position: "append" },
                event
              )
            }
            className={joinClassNames(
              "flex min-h-0 flex-1 flex-col items-center justify-center gap-2 border-2 border-dashed border-kumo-line text-xs text-kumo-subtle",
              activeDrop?.target.groupId === group.id
                ? "border-kumo-brand bg-kumo-fill/30"
                : ""
            )}
          >
            <span>Drop a tab here</span>
            {onCloseGroup && groupIds.length > 1 ? (
              <button
                type="button"
                data-editor-layout-action="close-empty-group"
                onClick={() => onCloseGroup(group.id)}
                className="rounded border border-kumo-line px-2 py-0.5 text-xs text-kumo-subtle hover:bg-kumo-fill hover:text-kumo-default"
              >
                Close this split
              </button>
            ) : null}
          </div>
        ) : (
          <div
            id={`${instanceId}-surface-${domIdPart(group.id)}`}
            data-editor-layout-surface=""
            className="relative min-h-0 flex-1"
          />
        )}
      </section>
    );
  }

  function renderNode(currentNode: EditorLayoutNode, isRoot: boolean): ReactNode {
    if (currentNode.kind === "group") {
      return renderGroup(currentNode, isRoot);
    }
    const firstChild = currentNode.children[0];
    const secondChild = currentNode.children[1];
    return (
      <div
        key={currentNode.id}
        data-editor-layout-split=""
        data-split-id={currentNode.id}
        className="h-full min-h-0 min-w-0"
      >
        <SplitPane
          direction={currentNode.direction}
          ratio={currentNode.ratio}
          defaultRatio={defaultSplitRatio}
          min={splitMin}
          max={splitMax}
          onRatioChange={(ratio) => onSplitRatioChange?.(currentNode.id, ratio)}
          separatorLabel={`Resize editor split ${currentNode.id}`}
        >
          {[
            renderNode(firstChild, false),
            renderNode(secondChild, false)
          ]}
        </SplitPane>
      </div>
    );
  }

  return (
    <div
      ref={bindLayout}
      data-editor-layout=""
      aria-label={ariaLabel}
      className={joinClassNames("relative h-full min-h-0 min-w-0", className)}
    >
      {renderNode(node, true)}
      {stableSurfaceHost
        ? tabs.map((tab) => {
        const tabId = getTabId(tab);
        const ownerId = groupIds.find((groupId) =>
          findGroup(node, groupId)?.tabs.includes(tabId)
        );
        if (!ownerId) return null;
        const owner = findGroup(node, ownerId);
        if (!owner) return null;
        const active = owner.activeTabId === tabId;
        const focused = activeFocusedGroupId === ownerId;
        const panelId = `${instanceId}-panel-${domIdPart(tabId)}`;
        const tabDomId = `${instanceId}-tab-${domIdPart(ownerId)}-${domIdPart(tabId)}`;
        return (
          <StableTabPanel
            key={tabId}
            host={stableSurfaceHost}
            container={layoutRoot}
            destinationId={`${instanceId}-surface-${domIdPart(ownerId)}`}
            panelId={panelId}
            tabDomId={tabDomId}
            active={active}
            onFocus={() => focusGroup(ownerId)}
          >
            {renderTab(tab, {
              tab,
              tabId,
              groupId: ownerId,
              panelId,
              active,
              focused
            })}
          </StableTabPanel>
        );
      })
        : null}
      {groupMenu !== null
        ? createPortal(
            <div
              className="fixed inset-0 z-[10000]"
              onClick={() => setGroupMenu(null)}
              onContextMenu={(event) => {
                event.preventDefault();
                setGroupMenu(null);
              }}
            >
              <div
                role="menu"
                aria-label="Editor group actions"
                style={{ left: groupMenu.x, top: groupMenu.y }}
                className="fixed z-[10001] min-w-[10rem] rounded-md border border-kumo-line bg-kumo-base p-1 shadow-xl"
                onClick={(event) => event.stopPropagation()}
              >
                <button
                  type="button"
                  role="menuitem"
                  disabled={!onSplit}
                  onClick={() => {
                    onSplit?.(groupMenu.groupId, "vertical");
                    setGroupMenu(null);
                  }}
                  className="flex w-full items-center gap-2 rounded px-2 py-1 text-left text-xs text-kumo-default hover:bg-kumo-fill disabled:opacity-40"
                >
                  <ColumnsPlusRightIcon size={14} aria-hidden="true" /> Split right
                </button>
                <button
                  type="button"
                  role="menuitem"
                  disabled={!onSplit}
                  onClick={() => {
                    onSplit?.(groupMenu.groupId, "horizontal");
                    setGroupMenu(null);
                  }}
                  className="flex w-full items-center gap-2 rounded px-2 py-1 text-left text-xs text-kumo-default hover:bg-kumo-fill disabled:opacity-40"
                >
                  <RowsPlusBottomIcon size={14} aria-hidden="true" /> Split down
                </button>
                {onCloseGroup && groupIds.length > 1 ? (
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      onCloseGroup(groupMenu.groupId);
                      setGroupMenu(null);
                    }}
                    className="flex w-full items-center gap-2 rounded px-2 py-1 text-left text-xs text-kumo-default hover:bg-kumo-fill"
                  >
                    × Close this split
                  </button>
                ) : null}
                {renderMenuItems ? (
                  <>
                    <div className="mx-1 my-1 h-px bg-kumo-line" aria-hidden="true" />
                    {renderMenuItems({
                      groupId: groupMenu.groupId,
                      close: () => setGroupMenu(null)
                    })}
                  </>
                ) : null}
              </div>
            </div>,
            document.body
          )
        : null}
    </div>
  );
}

export { EditorLayout as EditorLayoutHost };
